import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { CommandResult } from './interfaces';
import { TranscriptEditorChangeType } from '../types';
import { TextElement } from '../models/TranscriptElement';

/**
 * Command for processing forced alignment results on text elements
 * 
 * This command handles updating elements with timing data from the 
 * alignment service, ensuring proper undo/redo functionality.
 */
export class ProcessAlignmentResultCommand extends AbstractTranscriptEditorCommand {
  /**
   * ID of the segment containing the element to update
   */
  private segmentId: string;
  
  /**
   * ID of the paragraph containing the element to update
   */
  private paragraphId: string;
  
  /**
   * ID of the element to update with alignment data
   */
  private elementId: string;
  
  /**
   * New text to apply to the element
   */
  private newText: string;
  
  /**
   * New start time to apply to the element
   */
  private startTime: number;
  
  /**
   * New end time to apply to the element
   */
  private endTime: number;
  
  /**
   * Original text before update (stored for undo)
   */
  private originalText: string = '';
  
  /**
   * Original start time before update (stored for undo)
   */
  private originalStartTime: number | undefined;
  
  /**
   * Original end time before update (stored for undo)
   */
  private originalEndTime: number | undefined;
  
  /**
   * Create a new ProcessAlignmentResultCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to update with alignment data
   * @param newText New text content to apply to the element
   * @param startTime New start time to apply to the element
   * @param endTime New end time to apply to the element
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    newText: string,
    startTime: number,
    endTime: number
  ) {
    super('Process Alignment Result', editor);
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.newText = newText;
    this.startTime = startTime;
    this.endTime = endTime;
  }
  
  /**
   * Execute the command
   * 
   * @returns True if the command was executed successfully
   */
  execute(): CommandResult {
    try {
      
      // Get the container, segment, paragraph, and element
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      const element = paragraph.getElement(this.elementId) as TextElement;
      if (!element || element.type !== 'text') {
        return false;
      }
      
      // Store original values for undo
      this.originalText = element.value;
      this.originalStartTime = element.startTime;
      this.originalEndTime = element.endTime;
      
      // Update the element with new values
      element.value = this.newText;
      element.startTime = this.startTime;
      element.endTime = this.endTime;
      
      // Notify observers about the update
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segment,
        paragraph,
        element,
        oldText: this.originalText,
        newText: this.newText,
        source: 'alignment'
      });
      
      return true;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns True if the command was undone successfully
   */
  undo(): CommandResult {
    try {
      // Get the container, segment, paragraph, and element
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      const element = paragraph.getElement(this.elementId) as TextElement;
      if (!element || element.type !== 'text') {
        return false;
      }
      
      // Restore original values
      const newText = element.value; // Current text is the new text for notification
      element.value = this.originalText;
      element.startTime = this.originalStartTime;
      element.endTime = this.originalEndTime;
      
      // Notify observers about the update
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segment,
        paragraph,
        element,
        oldText: newText,
        newText: this.originalText,
        source: 'alignment_undo'
      });
      
      return true;
    } catch (error) {
      return false;
    }
  }
}